import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, X, Package } from "@phosphor-icons/react";

function getInitialChannel(data) {
  if (!data) return "walk_in";
  if (data.channel) return data.channel;
  if (data.source === "meta_ads" || data.source === "meta") return "meta";
  if (data.source === "indiamart") return "indiamart";
  if (data.source === "telephonic") return "telephonic";
  if (data.source === "csv_upload") return "csv_upload";
  if (data.source === "walk_in") return "walk_in";
  return data.source || "walk_in";
}

export default function AddLeadModal({ onClose, salespeople = [], role, onCreated, initialData = null }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    name: initialData?.name || "",
    phone: initialData?.phone && initialData.phone !== "—" ? initialData.phone : "",
    email: initialData?.email || "",
    company: initialData?.company || "",
    product_code: initialData?.product_code || "",
    product_interest: initialData?.product_interest || "",
    city: initialData?.city || "",
    notes: initialData?.notes || "",
    source: "manual",
    assigned_to: initialData?.assigned_to || "",
    channel: getInitialChannel(initialData)
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products")
      .then((res) => {
        setProducts(res.data || []);
        // Auto-match product_code if initialData had product_interest but no product_code
        if (initialData?.product_interest && !initialData?.product_code && res.data?.length) {
          const match = res.data.find(
            p => p.productName.toLowerCase() === initialData.product_interest.toLowerCase()
          );
          if (match) {
            setForm(prev => ({ ...prev, product_code: match.productCode }));
          }
        }
      })
      .catch(() => { });
  }, [initialData]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.product_code == null || form.product_code === "") {
      toast.error("Please select a product");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.assigned_to) delete payload.assigned_to;
      if (payload.product_code != null && payload.product_code !== "") {
        const pnum = parseInt(payload.product_code, 10);
        if (!isNaN(pnum)) payload.product_code = pnum;
      }
      const res = await api.post("/leads", payload);
      toast.success(initialData ? "Duplicate lead created" : "Lead created");
      if (onCreated) onCreated(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save lead");
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-3 sm:p-6 animate-fadeIn" onClick={onClose}>
      <div
        className="bg-white border border-[#D1D5DB] w-full max-w-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-lead-modal"
      >
        {/* Modal Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-[#E5E7EB] bg-gradient-to-r from-slate-50 to-white">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-heading font-black text-xl sm:text-2xl text-[#111827] tracking-tight">
                {initialData ? "Duplicate Lead" : "Add New Lead"}
              </h3>
              {initialData && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider bg-blue-50 text-[#002FA7] border border-blue-200 px-2 py-0.5 rounded">
                  <Copy size={12} weight="bold" /> Duplicating
                </span>
              )}
            </div>
            <p className="text-xs text-[#6B7280] mt-0.5">
              {initialData
                ? `Details auto-filled from "${initialData.name}". Adjust any fields as required.`
                : "Manually capture a lead. Follow-up schedule will be calculated automatically."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#111827] p-2 hover:bg-slate-100 transition-colors"
            title="Close"
            data-testid="close-lead-modal-btn"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 sm:p-7 overflow-y-auto flex-1 space-y-5 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {[
              ["name", "Full Name", true, "text", "e.g. John Doe"],
              ["phone", "Phone / WhatsApp", true, "tel", "10-digit phone number"],
              ["email", "Email Address", false, "email", "client@example.com"],
              ["company", "Company / Business Name", false, "text", "e.g. Acme Corp"],
              ["city", "City / Location", false, "text", "e.g. Ahmedabad"]
            ].map(([k, label, req, inputType, placeholder]) => (
              <div key={k}>
                <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                  {label} {req && <span className="text-red-500 font-bold">*</span>}
                </label>
                <input
                  type={inputType}
                  placeholder={placeholder}
                  value={form[k] != null ? form[k] : ""}
                  onChange={(e) => set(k, e.target.value)}
                  required={!!req}
                  data-testid={`add-lead-${k}`}
                  className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400"
                />
              </div>
            ))}

            {/* Product Dropdown */}
            <div className="sm:col-span-1">
              <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                Product <span className="text-red-500 font-bold">*</span>
              </label>
              <select
                required
                value={form.product_code != null ? String(form.product_code) : ""}
                onChange={(e) => {
                  const code = e.target.value;
                  const matched = products.find((p) => String(p.productCode) === String(code));
                  const numCode = code ? parseInt(code, 10) : "";
                  setForm((prev) => ({
                    ...prev,
                    product_code: isNaN(numCode) ? code : numCode,
                    product_interest: matched ? matched.productName : prev.product_interest
                  }));
                }}
                data-testid="add-lead-product-code"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all font-medium"
              >
                <option value="">Select Product...</option>
                {products.map((p) => (
                  <option key={p.id || p.productCode} value={p.productCode}>
                    {p.productName}
                  </option>
                ))}
              </select>
            </div>

            {/* Product Details / Custom Interest */}
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                Product Details (Optional)
              </label>
              <input
                type="text"
                value={form.product_interest || ""}
                onChange={(e) => set("product_interest", e.target.value)}
                placeholder="e.g. Model, variant, or custom specifications"
                data-testid="add-lead-product_interest"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                Notes & Remarks
              </label>
              <textarea
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={3}
                placeholder="Any specific customer requirements, budget, or notes..."
                data-testid="add-lead-notes"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400 resize-none"
              />
            </div>

            <div className="sm:col-span-1">
              <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                Lead Channel
              </label>
              <select
                value={form.channel}
                onChange={(e) => set("channel", e.target.value)}
                data-testid="add-lead-channel"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all font-medium"
              >
                <option value="walk_in">Walk In</option>
                <option value="telephonic">Telephonic</option>
                <option value="meta">Meta</option>
                <option value="indiamart">IndiaMart</option>
                <option value="csv_upload">CSV Upload</option>
                <option value="other">Other</option>
              </select>
              <p className="text-[11px] text-[#6B7280] mt-1">
                {role === "salesperson"
                  ? "Select the lead acquisition channel."
                  : "Routes this lead to salespeople assigned to this channel."}
              </p>
            </div>

            {role !== "salesperson" && (
              <div className="sm:col-span-1">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                  Assign to (Optional)
                </label>
                <select
                  value={form.assigned_to}
                  onChange={(e) => set("assigned_to", e.target.value)}
                  data-testid="add-lead-assignee"
                  className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all font-medium"
                >
                  <option value="">Auto (Least loaded)</option>
                  {salespeople.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-[#6B7280] mt-1">Leave empty for automatic round-robin assignment.</p>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 pt-5 border-t border-[#E5E7EB]">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="add-lead-submit"
              className="w-full sm:w-auto bg-[#002FA7] text-white font-bold px-7 py-2.5 text-sm hover:bg-[#002280] disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2 shadow-sm"
            >
              {initialData && <Copy size={16} weight="bold" />}
              {saving ? "Saving..." : initialData ? "Create Duplicate Lead" : "Save Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
